import React, { useState, useEffect } from 'react';
import { userService, User, CreateUserRequest, UpdateUserRequest } from '../services/userService';
import { useModal } from '../contexts/ModalContext';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import ToggleSwitch from '../components/Common/ToggleSwitch';
import './Users.css';

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<CreateUserRequest>({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    isAdmin: false
  });
  const { showModal } = useModal();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAllUsers();
      setUsers(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData: UpdateUserRequest = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          isActive: editingUser.isActive,
          isAdmin: formData.isAdmin
        };
        await userService.updateUser(editingUser.id, updateData);
        showModal({
          title: 'Utilisateur mis à jour',
          message: `L'utilisateur "${formData.firstName} ${formData.lastName}" a été mis à jour avec succès.`,
          type: 'success',
          confirmText: 'OK'
        });
      } else {
        await userService.createUser(formData);
        showModal({
          title: 'Utilisateur créé',
          message: `L'utilisateur "${formData.firstName} ${formData.lastName}" a été créé avec succès.`,
          type: 'success',
          confirmText: 'OK'
        });
      }
      setShowForm(false);
      setEditingUser(null);
      setFormData({ email: '', firstName: '', lastName: '', password: '', isAdmin: false });
      loadUsers();
    } catch (err: any) {
      showModal({
        title: 'Erreur de sauvegarde',
        message: err.response?.data?.message || 'Erreur lors de la sauvegarde',
        type: 'error',
        confirmText: 'OK'
      });
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      password: '',
      isAdmin: user.isAdmin
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    const user = users.find(u => u.id === id);
    showModal({
      title: 'Confirmer la suppression',
      message: `Êtes-vous sûr de vouloir supprimer l'utilisateur "${user?.firstName} ${user?.lastName}" ? Cette action est irréversible.`,
      type: 'confirm',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      onConfirm: async () => {
        try {
          await userService.deleteUser(id);
          loadUsers();
          showModal({
            title: 'Utilisateur supprimé',
            message: 'L\'utilisateur a été supprimé avec succès.',
            type: 'success',
            confirmText: 'OK'
          });
        } catch (err: any) {
          showModal({
            title: 'Erreur de suppression',
            message: err.response?.data?.message || 'Erreur lors de la suppression',
            type: 'error',
            confirmText: 'OK'
          });
        }
      }
    });
  };

  const handleToggleStatus = async (id: number) => {
    const user = users.find(u => u.id === id);
    const action = user?.isActive ? 'désactiver' : 'activer';
    
    showModal({
      title: `Confirmer ${action}`,
      message: `Êtes-vous sûr de vouloir ${action} l'utilisateur "${user?.firstName} ${user?.lastName}" ?`,
      type: 'confirm',
      confirmText: action === 'activer' ? 'Activer' : 'Désactiver',
      cancelText: 'Annuler',
      onConfirm: async () => {
        try {
          await userService.toggleUserStatus(id);
          loadUsers();
          showModal({
            title: `Utilisateur ${action}`,
            message: `L'utilisateur a été ${action} avec succès.`,
            type: 'success',
            confirmText: 'OK'
          });
        } catch (err: any) {
          showModal({
            title: 'Erreur de changement de statut',
            message: err.response?.data?.message || 'Erreur lors du changement de statut',
            type: 'error',
            confirmText: 'OK'
          });
        }
      }
    });
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="users">
      <div className="users-header">
        <h2>Gestion des utilisateurs</h2>
        <button 
          className="btn btn-primary"
          onClick={() => {
            setEditingUser(null);
            setFormData({ email: '', firstName: '', lastName: '', password: '', isAdmin: false });
            setShowForm(true);
          }}
        >
          Ajouter un utilisateur
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {showForm && (
        <div className="user-form">
          <h3>{editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Prénom</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Nom</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            {!editingUser && (
              <div className="form-group">
                <label>Mot de passe</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </div>
            )}
            <div className="form-group">
              <label>Administrateur</label>
              <div className="toggle-container">
                <ToggleSwitch
                  checked={formData.isAdmin}
                  onChange={(checked) => setFormData({ ...formData, isAdmin: checked })}
                  size="medium"
                  labels={{ on: 'Oui', off: 'Non' }}
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingUser ? 'Modifier' : 'Créer'}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowForm(false)}
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="users-list">
        <table>
          <thead>
            <tr>
              <th>Nom complet</th>
              <th>Email</th>
              <th>Type</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.firstName} {user.lastName}</td>
                <td>{user.email}</td>
                <td>
                  <span className={`role ${user.isAdmin ? 'admin' : 'user'}`}>
                    {user.isAdmin ? 'Administrateur' : 'Utilisateur'}
                  </span>
                </td>
                <td>
                  <span className={`status ${user.isActive ? 'active' : 'inactive'}`}>
                    {user.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td>
                  <button 
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleEdit(user)}
                  >
                    Modifier
                  </button>
                  <button 
                    className="btn btn-sm btn-warning"
                    onClick={() => handleToggleStatus(user.id)}
                  >
                    {user.isActive ? 'Désactiver' : 'Activer'}
                  </button>
                  <button 
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(user.id)}
                  >
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Users;
